import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { ArrowUpRight, Check } from "lucide-react";

const ACADEMY_URL = "https://academy.fundai.co.in";
const REGISTER_URL = "https://academy.fundai.co.in/student/register";
const LOGIN_URL = "https://academy.fundai.co.in/student/login";

const CATEGORIES = ["All Courses", "AI & Build", "Design & Marketing", "Finance & Excel"];

const COURSES = [
  { code: "FA-AI-101", cat: "AI & Build", level: "Beginner", modules: 10, title: "AI Tools for Everyday Work", desc: "Learn to use AI for communication, research, documents, data and everyday productivity.", hours: "8–10 hours", fee: "₹999" },
  { code: "FA-PE-201", cat: "AI & Build", level: "Beginner to Intermediate", modules: 10, title: "Prompt Engineering Professional", desc: "Build precise and reusable prompt systems for research, business, content and product work.", hours: "12–15 hours", fee: "₹1,999" },
  { code: "FA-UX-101", cat: "Design & Marketing", level: "Beginner", modules: 10, title: "UI/UX & Landing Page Foundations", desc: "Turn a real user problem into an accessible and responsive landing-page experience.", hours: "10–12 hours", fee: "₹999" },
  { code: "FA-WD-201", cat: "AI & Build", level: "Beginner to Intermediate", modules: 14, title: "AI Website & Web Application Design", desc: "Plan, generate, test and present responsive frontend applications using AI-assisted tools.", hours: "20–24 hours", fee: "₹2,999" },
  { code: "FA-DM-101", cat: "Design & Marketing", level: "Beginner", modules: 10, title: "Digital Marketing Foundations", desc: "Create an ethical and measurable marketing plan covering content, search, advertising and CRM.", hours: "10–12 hours", fee: "₹999" },
  { code: "FA-PF-101", cat: "Finance & Excel", level: "Beginner", modules: 12, title: "Personal Finance Foundations", desc: "Understand budgeting, debt, insurance, investments, retirement planning and financial safety.", hours: "12–14 hours", fee: "₹999", finance: true },
  { code: "FA-XL-101", cat: "Finance & Excel", level: "Beginner", modules: 10, title: "Microsoft Excel Foundations", desc: "Build formula-driven spreadsheets, summaries, charts and practical finance workbooks.", hours: "12–14 hours", fee: "₹999" },
  { code: "FA-XL-301", cat: "Finance & Excel", level: "Intermediate", modules: 12, title: "Advanced Excel for Finance & Business Analytics", desc: "Learn dynamic formulas, Power Query, financial models, scenario analysis and dashboards.", hours: "18–22 hours", fee: "₹1,499" },
  { code: "FA-SM-201", cat: "Finance & Excel", level: "Beginner", modules: 14, title: "Stock Market, PMS & AIF Foundations", desc: "Understand market structure, investment analysis, portfolio risk, PMS and AIF fundamentals responsibly.", hours: "18–22 hours", fee: "₹1,499", finance: true },
  { code: "FA-DEV-301", cat: "AI & Build", level: "Intermediate", modules: 16, title: "AI Application Development, APIs, GitHub & Google Cloud", desc: "Build an AI application, connect APIs, manage the project through GitHub and understand secure cloud deployment.", hours: "36–42 hours", fee: "₹4,999" },
];

const HIGHLIGHTS = [
  { n: "10", label: "Practical courses" },
  { n: "118", label: "Complete learning modules" },
  { n: "590", label: "Graded quiz questions" },
  { n: "952", label: "Curriculum concepts" },
];

const HOW = [
  { n: "01", title: "Create Your Account", desc: "Register and explore the public learner demo." },
  { n: "02", title: "Choose and Purchase a Course", desc: "Complete payment securely through the Academy platform." },
  { n: "03", title: "Learn and Practise", desc: "Complete lectures, audio explanations, activities, quizzes and capstone work." },
  { n: "04", title: "Earn Your Certificate", desc: "The Super Admin reviews course completion and approves eligible certificates." },
];

const FEATURES = [
  "Self-paced structured learning",
  "Readable lectures with practical examples",
  "Audio-supported lessons",
  "Module-wise quizzes",
  "Sequential module unlocking",
  "Progress tracking",
  "Personal notes and activities",
  "Practical capstone projects",
  "AI Build Lab on selected courses",
  "Reviewed course-completion certificates",
];

export default function Academy() {
  const [cat, setCat] = useState("All Courses");

  useEffect(() => {
    document.title = "FundAI Academy | Practical AI, Technology, Finance & Business Courses";
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute("content", "FundAI Academy offers structured, self-paced courses in AI, technology, finance, marketing and Excel — with lectures, activities, quizzes, capstone projects and reviewed completion certificates.");
    window.scrollTo(0, 0);
    return () => { if (desc && original) desc.setAttribute("content", original); };
  }, []);

  const filtered = useMemo(() => cat === "All Courses" ? COURSES : COURSES.filter((c) => c.cat === cat), [cat]);

  return (
    <div className="relative">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-24 md:pt-48 md:pb-32 overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-40" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] glow-blue" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <span className="h-px w-10 bg-[#0057FF]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00D1FF]">FundAI Academy</span>
              </div>
              <h1 className="font-head font-light tracking-tight leading-[1.02] text-4xl md:text-6xl lg:text-[4.5rem] text-metallic max-w-5xl">
                Learn Practical AI, Technology, Finance and Business Skills.
              </h1>
              <p className="mt-8 max-w-3xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">
                FundAI Academy offers structured, self-paced courses with complete lectures, practical examples, guided activities, quizzes, capstone projects and reviewed course-completion certificates.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a href={ACADEMY_URL} className="group inline-flex items-center justify-center gap-2 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-7 py-3.5 font-medium transition-colors active:scale-[0.98]" data-testid="academy-explore-btn">
                  Explore FundAI Academy <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <a href={REGISTER_URL} className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white rounded-full px-7 py-3.5 font-medium transition-colors" data-testid="academy-register-btn">
                  Create Student Account
                </a>
              </div>
              <p className="mt-5 text-xs text-zinc-500 leading-relaxed max-w-2xl">
                Students can create an account and explore the demo. Full courses are unlocked only after verified payment.
              </p>
            </Reveal>
          </div>
        </section>

        {/* Highlights */}
        <section className="border-y border-white/5 bg-[#08080B]" data-testid="academy-highlights">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Academy Highlights</span>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
              {HIGHLIGHTS.map((h) => (
                <div key={h.label}>
                  <div className="font-head text-3xl md:text-5xl font-light text-metallic">{h.n}</div>
                  <div className="mt-1 text-sm text-zinc-500">{h.label}</div>
                </div>
              ))}
            </div>
            <ul className="mt-8 grid sm:grid-cols-3 gap-3 text-sm text-zinc-400">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#00D1FF] mt-0.5 shrink-0" /> Guided activities and capstone projects</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#00D1FF] mt-0.5 shrink-0" /> AI Build Lab access on selected courses</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#00D1FF] mt-0.5 shrink-0" /> Course-completion certificates subject to assessment and approval</li>
            </ul>
          </div>
        </section>

        {/* Catalogue */}
        <section id="catalogue" className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <SectionHeading label="Course Catalogue" title="Ten practical courses. One clear path." className="max-w-3xl" />
            </Reveal>

            <div className="mt-10 flex flex-wrap gap-2" data-testid="academy-filter-tabs">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`text-xs md:text-sm px-4 py-2 rounded-full border transition-colors ${cat === c ? "bg-[#0057FF] border-[#0057FF] text-white" : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"}`} data-testid={`academy-filter-${c.replace(/\W/g, "").toLowerCase()}`}>
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              {filtered.map((c, i) => (
                <Reveal key={c.code} delay={(i % 2) * 0.05}>
                  <div className="card-hover h-full flex flex-col rounded-2xl border border-white/5 bg-[#0C0C10] p-7 hover:border-[#0057FF]/40" data-testid={`academy-course-${c.code}`}>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <span className="font-mono text-[11px] text-zinc-500">{c.code}</span>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.level}</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5">{c.modules} modules · {c.hours}</div>
                      </div>
                    </div>
                    <h3 className="font-head text-xl md:text-2xl font-medium leading-snug">{c.title}</h3>
                    <p className="mt-3 text-base text-zinc-400 leading-relaxed flex-1">{c.desc}</p>
                    {c.finance && (
                      <p className="mt-4 text-[11px] text-zinc-600 leading-relaxed border-l-2 border-white/10 pl-3">
                        FundAI Academy provides educational content only. It does not provide personalised investment advice or guarantee financial returns.
                      </p>
                    )}
                    <div className="mt-6 flex items-end justify-between border-t border-white/5 pt-5 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Course fee</div>
                        <div className="font-head text-xl text-white mt-1">{c.fee}<span className="text-xs text-zinc-500 font-body font-normal ml-2">incl. GST</span></div>
                      </div>
                      <a href={ACADEMY_URL} className="group inline-flex items-center gap-2 bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors active:scale-[0.98]" data-testid={`academy-open-${c.code}`}>
                        Open Academy <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* How Learning Works */}
        <section className="relative py-24 md:py-32 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal>
              <SectionHeading label="How Learning Works" title="From account to certificate — in four steps." className="max-w-3xl" />
            </Reveal>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {HOW.map((h, i) => (
                <Reveal key={h.n} delay={i * 0.06}>
                  <div className="card-hover h-full rounded-2xl border border-white/5 bg-[#0C0C10] p-7 hover:border-white/15" data-testid={`academy-how-${h.n}`}>
                    <span className="font-mono text-sm text-[#0057FF]">{h.n}</span>
                    <h3 className="font-head text-lg font-medium mt-3">{h.title}</h3>
                    <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{h.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="relative py-24 md:py-32 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_1.4fr] gap-12">
            <Reveal>
              <SectionHeading label="Learner Features" title="Everything you need to actually learn." />
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                    <Check className="h-4 w-4 text-[#00D1FF] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        {/* Access Explanation */}
        <section className="relative py-24 md:py-32 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Reveal><SectionHeading label="Access" title="Free to explore. Paid to complete." className="max-w-3xl" /></Reveal>
            <div className="mt-12 grid md:grid-cols-2 gap-5">
              <Reveal>
                <div className="h-full rounded-2xl border border-white/5 bg-[#0C0C10] p-8" data-testid="academy-access-free">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Free demo access</span>
                  <h3 className="font-head text-2xl font-medium mt-3">Explore before you enrol.</h3>
                  <p className="mt-3 text-base text-zinc-400 leading-relaxed">
                    Students may create an account, view the catalogue and explore the learner demo without payment.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.08}>
                <div className="h-full rounded-2xl border border-[#0057FF]/40 bg-[#0057FF]/5 p-8" data-testid="academy-access-paid">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00D1FF]">Paid course access</span>
                  <h3 className="font-head text-2xl font-medium mt-3">Unlock the full course after payment.</h3>
                  <p className="mt-3 text-base text-zinc-400 leading-relaxed">
                    Full lectures, quizzes, saved progress, Build Lab access, capstones and certificates are unlocked only after successful payment verification.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-28 md:py-40 border-t border-white/5 overflow-hidden">
          <div className="absolute inset-0 grid-lines opacity-30" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] glow-blue" />
          <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-head text-4xl md:text-6xl font-light tracking-tight text-metallic leading-[1.05]">
              Start building skills<br />that you can apply.
            </h2>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
              <a href={REGISTER_URL} className="bg-[#0057FF] hover:bg-[#0047D6] text-white rounded-full px-7 py-3.5 font-medium transition-colors active:scale-[0.98]" data-testid="academy-final-register">Create Student Account</a>
              <a href={LOGIN_URL} className="border border-white/15 hover:border-white/40 text-white rounded-full px-7 py-3.5 font-medium transition-colors" data-testid="academy-final-login">Student Login</a>
              <a href={ACADEMY_URL} className="text-zinc-300 hover:text-white rounded-full px-5 py-3.5 font-medium transition-colors" data-testid="academy-final-explore">Explore Academy</a>
            </div>
            <p className="mt-8 text-xs text-zinc-600 leading-relaxed max-w-2xl mx-auto">
              FundAI Academy provides educational content only. It does not guarantee employment, placement, income or investment returns. Certificates are subject to completion and human review.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
